/** Shape of one selectable option inside a filter section. */
export interface FilterOption {
  value: string;
  label: string;
  selected: boolean;
}

/**
 * One filter section. `availableValues` is populated from the items actually on screen, so a
 * section never offers a value that would come back empty.
 */
export interface FilterDefinition<T> {
  id: string;
  label: string;
  getValue: (item: T) => string | string[] | null | undefined;
  availableValues: string[];
  getLabel: (value: string) => string;
  /** Option ordering; defaults to a numeric-aware label comparison. */
  compareOptions?: (left: FilterOption, right: FilterOption) => number;
  /**
   * Section that picks rather than narrows, e.g. a sort order. Exactly one value stays selected,
   * its options are supplied by the caller, and it is left out of the chips and the applied count.
   */
  isChoice?: boolean;
  /** Condensed label for a choice section's row pill; defaults to `getLabel`. */
  getChoiceLabel?: (value: string) => string;
}

export interface FilterChip {
  filterId: string;
  filterLabel: string;
  value: string;
  displayLabel: string;
}

export type FilterSelection = Record<string, string[]>;

/** Values are compared as trimmed strings because sources like EDH return numbers for string fields. */
function valuesOf<T>(definition: FilterDefinition<T>, item: T): string[] {
  const raw = definition.getValue(item);

  return (Array.isArray(raw) ? raw : [raw])
    .map(value => `${value ?? ""}`.trim())
    .filter(value => value.length > 0);
}

export function collectAvailableValues<T>(items: readonly T[], definitions: FilterDefinition<T>[]): void {
  for (const definition of definitions) {
    // A choice section's options are fixed by the caller rather than derived from the items.
    if (definition.isChoice) {
      continue;
    }

    const values = new Set<string>();

    for (const item of items) {
      for (const value of valuesOf(definition, item)) {
        values.add(value);
      }
    }

    definition.availableValues = Array.from(values)
      .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
  }
}

/** OR within a section, AND across sections. */
export function filterItems<T>(
  items: readonly T[],
  definitions: FilterDefinition<T>[],
  selected: FilterSelection
): T[] {
  const filterDefinitions = definitions.filter(definition => !definition.isChoice);

  if (!filterDefinitions.some(definition => selected[definition.id]?.length)) {
    return [...items];
  }

  return items.filter(item =>
    filterDefinitions.every(definition => {
      const selectedForSection = selected[definition.id];

      if (!selectedForSection?.length) {
        return true;
      }

      const itemValues = valuesOf(definition, item);

      return selectedForSection.some(value => itemValues.includes(value));
    }));
}

export function countSelected<T>(definitions: FilterDefinition<T>[], selected: FilterSelection): number {
  return definitions
    .filter(definition => !definition.isChoice)
    .reduce((total, definition) => total + (selected[definition.id]?.length ?? 0), 0);
}

export function toFilterChips<T>(definitions: FilterDefinition<T>[], selected: FilterSelection): FilterChip[] {
  return definitions
    .filter(definition => !definition.isChoice)
    .flatMap(definition =>
      (selected[definition.id] ?? []).map(value => ({
        filterId: definition.id,
        filterLabel: definition.label,
        value,
        displayLabel: definition.getLabel(value)
      })));
}

export function removeSelectedValue(
  selected: FilterSelection,
  filterId: string,
  value: string
): FilterSelection {
  return { ...selected, [filterId]: (selected[filterId] ?? []).filter(v => v !== value) };
}
